import React, { useState, useEffect } from 'react';

import 'react-dates/initialize';
import { DateRangePicker } from 'react-dates';
import SearchTermsAutocomplete from './SearchTermsAutocomplete';

import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import moment from 'moment';
moment.locale(freedomeConfig.locale);
/**/
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { useBlockScroll } from '../../hooks/useBlockScroll';
import {
  DATE_FORMATS,
  DROPDOWN_MBL_BEHAVIOUR,
  QUERY_PARAMS,
} from '../../../constants';
import { useDaySize } from '../../hooks/useDaySize';

import { Icon, InlineIcon } from '@iconify/react';
import iconCalendar from '@iconify-icons/mdi/calendar-today';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import chosenDateMethods from '../../hooks/interactsWithChosenDate';

const _DEFAULT_PLACEHOLDER_TEXT = app_main_wpml.search_when_placeholder;
const _ALWAYS_PLACEHOLDER = app_main_wpml.always;
const _TOMORROW_PLACEHOLDER = app_main_wpml.tomorrow;
const _AFTER_TOMORROW_PLACEHOLDER = app_main_wpml.after_tomorrow;
const _NEXT_MONTH_PLACEHOLDER = app_main_wpml.next_month_full;
const _NEXT_TO_ME = app_main_wpml.close_to_me;
const _PLACEHOLDERS_ARRAY = [_ALWAYS_PLACEHOLDER, _TOMORROW_PLACEHOLDER, _AFTER_TOMORROW_PLACEHOLDER, _NEXT_MONTH_PLACEHOLDER];

const tomorrow = moment().add(1, 'days');
const after_tomorrow = moment().add(2, 'days');

const SearchForm = (props) => {
  const [userLocation, setUserLocation] = useState(JSON.parse(localStorage.getItem('userLocation')));
  const [isAlways, setIsAlways] = useState(props.isAlways);
  const [isNextMonth, setIsNextMonth] = useState(props.isNextMonth);

  const [placeholderText, setPlaceholderText] = React.useState(() => {
    if (isAlways) return _ALWAYS_PLACEHOLDER;
    if (isNextMonth && !(startDate || endDate)) return _NEXT_MONTH_PLACEHOLDER;
    return _DEFAULT_PLACEHOLDER_TEXT;
  });

  const isDeviceSmallerThanTablet = !useMatchesMinWidth(768);
  const [startDate, setStartDate] = React.useState(props.initialStartDate);
  const [endDate, setEndDate] = React.useState(props.initialEndDate);
  const [focusedInput, setFocusedInput] = React.useState(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);
  useBlockScroll(isDeviceSmallerThanTablet && isDatePickerOpen);

  const updateChosenDate = (startDate, endDate) => {
    if (startDate) {
      chosenDateMethods.writeSearchDate(startDate, endDate);
    } else {
      chosenDateMethods.clear();
    }
  };

  React.useEffect(() => {
    updateChosenDate(startDate, endDate);
    if (startDate || endDate) {
      if (startDate === endDate) {
        return;
      }
      setPlaceholderText(_DEFAULT_PLACEHOLDER_TEXT);
    }
  }, [startDate, endDate]);

  const daySize = useDaySize();

  const [selectedSearchTerm, setSelectedSearchTerm] = useState(props.initialSearchTerm);
  useEffect(() => { setSelectedSearchTerm(props.initialSearchTerm) }, [props.initialSearchTerm]);

  const handleSearchTermChange = (searchTerm) => {
    setSelectedSearchTerm(searchTerm);
  };

  const handleDatesChange = ({ startDate, endDate }) => {
    if (endDate && !startDate) {
      setStartDate(null);
      setEndDate(null);
    } else {
      setStartDate(startDate);
      setEndDate(endDate);
    }
  };
  const setDates = (start, end, placeholder, input) => {
    setStartDate(start);
    setEndDate(end);
    setPlaceholderText(placeholder);
    setFocusedInput(input);
  };

  return (
    <div className={classNames('search-section', props.className)}>
      <form
        className={'search-section__form'}
        method={'GET'}
        action={props.catalogueUrl}
      >
        <div
          className={classNames('search-section__location-input', {
            'search-section__location-input--icon-primary': !!selectedSearchTerm,
          })}
        >
          <SearchTermsAutocomplete
            selectedSearchTerm={selectedSearchTerm ? selectedSearchTerm : ''}
            onSearchTermSelect={handleSearchTermChange}
            setUserLocation={setUserLocation}
            scrollOnFocus={props.scrollOnFocus}
          />
        </div>
        {props.isDatepickerVisible === true && (
          <div className={classNames('date-range-container')}>
            <div
              className={classNames('date-range-container__placeholder', {
                'date-range-container__placeholder--visible':
                  (!startDate && !endDate) || (_PLACEHOLDERS_ARRAY.includes(placeholderText)),
                'date-range-container__placeholder--always':
                  _PLACEHOLDERS_ARRAY.includes(placeholderText)
              })}
            >
              <Icon icon={iconCalendar} className="icon" />
              {placeholderText}
            </div>
            <DateRangePicker
              daySize={daySize}
              renderCalendarInfo={renderCalendarInfo}
              weekDayFormat="ddd"
              startDate={startDate}
              endDate={endDate}
              startDateId=""
              endDateId=""
              onDatesChange={handleDatesChange}
              minimumNights={0}
              enableOutsideDays={true}
              withPortal={isDeviceSmallerThanTablet}
              displayFormat={DATE_FORMATS.SEARCH_FORM_FE_VISUALIZATION}
              onFocusChange={(focusedInput) => {
                if (['startDate', 'endDate'].includes(focusedInput)) {
                  setIsDatePickerOpen(true);
                } else {
                  setIsDatePickerOpen(false);
                }
                setFocusedInput(focusedInput);
              }}
              focusedInput={focusedInput}
              numberOfMonths={1}
              readOnly
              startDatePlaceholderText={app_main_wpml.search_form}
              endDatePlaceholderText={app_main_wpml.search_to}
              hideKeyboardShortcutsPanel={true}
            />
          </div>
        )}
        <input
          name={QUERY_PARAMS.SEARCH_TERM}
          value={selectedSearchTerm || ''}
          data-gtm-item="search-form"
          data-gtm-detail="search-term"
          hidden
          readOnly
        />
        {placeholderText === _ALWAYS_PLACEHOLDER && (
          <input name={QUERY_PARAMS.ALWAYS} value={true} hidden readOnly />
        )}
        {placeholderText !== _ALWAYS_PLACEHOLDER &&
          props.defaultDatesOnSubmit &&
          !startDate &&
          !endDate && (
            <input
              name={QUERY_PARAMS.NEXT_MONTH}
              value={true}
              hidden
              readOnly
            />
          )}
        <input
          hidden
          readOnly
          name={QUERY_PARAMS.START_DATE}
          value={
            startDate
              ? moment(startDate).format(
                DATE_FORMATS.SEARCH_FORM_BE_VISUALIZATION
              )
              : ''
          }
          data-gtm-item="search-form"
          data-gtm-detail="start_date"
        />
        <input
          hidden
          readOnly
          name={QUERY_PARAMS.END_DATE}
          value={
            endDate
              ? moment(endDate).format(
                DATE_FORMATS.SEARCH_FORM_BE_VISUALIZATION
              )
              : ''
          }
          data-gtm-item="search-form"
          data-gtm-detail="end_date"
        />
        <button
          className={classNames(
            'search-section__form__search-btn',
            'btn',
            'btn--primary',
            'btn--expanded',
            'btn--no-min-width'
          )}
          type={props.onSubmit ? 'button' : 'submit'}
          onClick={() => {
            if (props.onSubmit) {
              props.onSubmit(selectedSearchTerm, startDate, endDate, isAlways);
            }
          }}
        >
          {app_main_wpml.search_cta}
        </button>
      </form>
    </div>
  );

  function renderCalendarInfo() {
    return (
      <div className="DayPicker__calendar-info">
        <Button
          size={ButtonSizes.xs}
          style={ButtonStyles.chipSuggestion}
          label={app_main_wpml.always}
          onClick={() => {
            setDates(null, null, _ALWAYS_PLACEHOLDER, null);
            setIsAlways(true);
          }}
        />
        <Button
          size={ButtonSizes.xs}
          style={ButtonStyles.chipSuggestion}
          label={app_main_wpml.tomorrow}
          onClick={() => {
            setDates(tomorrow, tomorrow, _TOMORROW_PLACEHOLDER, null);
          }}
        />
        <Button
          size={ButtonSizes.xs}
          style={ButtonStyles.chipSuggestion}
          label={app_main_wpml.after_tomorrow}
          onClick={() => {
            setDates(
              after_tomorrow,
              after_tomorrow,
              _AFTER_TOMORROW_PLACEHOLDER,
              null
            );
          }}
        />
      </div>
    );
  }
};

SearchForm.propTypes = {
  className: PropTypes.string,
  onSubmit: PropTypes.func,
  catalogueUrl: PropTypes.string,
  initialStartDate: PropTypes.instanceOf(moment),
  initialEndDate: PropTypes.instanceOf(moment),
  initialSearchTerm: PropTypes.string,
  isAlways: PropTypes.bool.isRequired,
  isNextMonth: PropTypes.bool.isRequired,
  isDatepickerVisible: PropTypes.bool.isRequired,
  scrollOnFocus: PropTypes.bool,
  defaultDatesOnSubmit: PropTypes.bool,
};

SearchForm.defaultProps = {
  initialSearchTerm: '',
  isAlways: false,
  isNextMonth: false,
  isDatepickerVisible: true,
  scrollOnFocus: false,
  defaultDatesOnSubmit: false
};

SearchForm.sanitizeProps = (props) => ({
  ...props,
  isDatepickerVisible: props.isDatepickerVisible == '1',
  scrollOnFocus: props.scrollOnFocus == '1',
  defaultDatesOnSubmit: props.defaultDatesOnSubmit == '1',
  isAlways: props.isAlways == '1',
});

export default SearchForm;
